import { Box, Divider, Paper, Tab, Tabs, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { trackEvent } from "../../reporting/reporting";
import { useUser } from "../../utils/tokens";
import { ExportButton } from "./ExportButton";
import { FilterBar } from "./FilterBar";
import { buildMenuAnalyticsEventProps } from "./hooks/menuAnalyticsEvents";
import { useAnalyticsFilters } from "./hooks/useAnalyticsFilters";
import { useIngredientsAnalytics } from "./hooks/useIngredientsAnalytics";
import { useMenuItemsAnalytics } from "./hooks/useMenuItemsAnalytics";
import { useTableControls } from "./hooks/useTableControls";
import { IngredientsTable } from "./IngredientsTable";
import { MenuItemsTable } from "./MenuItemsTable";
import { SummaryCards } from "./SummaryCards";
import { IngredientsSortField, MenuItemsSortField, WeightUnit } from "./types";

export const MENU_ITEMS_TAB = "Menu Items";
export const INGREDIENTS_TAB = "Ingredients";

export const MenuAnalyticsDashboard = () => {
  const user = useUser();
  const [selectedTab, setSelectedTab] = useState<string>(MENU_ITEMS_TAB);
  const [weightUnit, setWeightUnit] = useState<WeightUnit>("lb");

  const { filters, filtersKey, setTimeRange, setCustomRange, setMealPeriod, setTeamId } = useAnalyticsFilters();

  // Fires once when the dashboard first mounts, capturing the initial filter
  // selections. Intentionally empty deps so it does not re-fire on tab switches
  // or filter changes (tab switches emit their own event below).
  useEffect(() => {
    trackEvent("Nutrition | Kitchen | Menu Analytics | Page Viewed", buildMenuAnalyticsEventProps(user, filters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (value: string) => {
    setSelectedTab(value);
    trackEvent("Nutrition | Kitchen | Menu Analytics | Tab Switched", {
      ...buildMenuAnalyticsEventProps(user, filters),
      tab: value,
    });
  };
  const menuItemsControls = useTableControls<MenuItemsSortField>({ defaultSortBy: "totalOrders", resetKey: filtersKey });
  const ingredientsControls = useTableControls<IngredientsSortField>({ defaultSortBy: "totalGramsServed", resetKey: filtersKey });

  const { data: menuItemsData, loading: menuItemsLoading, error: menuItemsError } = useMenuItemsAnalytics({
    ...filters,
    sortBy: menuItemsControls.sortBy,
    sortDir: menuItemsControls.sortDir,
    page: menuItemsControls.page,
    pageSize: menuItemsControls.pageSize,
  });

  const { data: ingredientsData, loading: ingredientsLoading, error: ingredientsError } = useIngredientsAnalytics(
    {
      ...filters,
      sortBy: ingredientsControls.sortBy,
      sortDir: ingredientsControls.sortDir,
      page: ingredientsControls.page,
      pageSize: ingredientsControls.pageSize,
    },
    { enabled: selectedTab === INGREDIENTS_TAB }
  );

  const isMenuItems = selectedTab === MENU_ITEMS_TAB;
  const exportButton = isMenuItems ? (
    <ExportButton
      tab="menu-items"
      filters={{ ...filters, sortBy: menuItemsControls.sortBy, sortDir: menuItemsControls.sortDir }}
      totalCount={menuItemsData?.meta.totalCount ?? 0}
      range={menuItemsData?.meta.range}
      disabled={menuItemsLoading || menuItemsError || (menuItemsData?.meta.totalCount ?? 0) === 0}
    />
  ) : (
    <ExportButton
      tab="ingredients"
      filters={{ ...filters, sortBy: ingredientsControls.sortBy, sortDir: ingredientsControls.sortDir }}
      totalCount={ingredientsData?.meta.totalCount ?? 0}
      range={ingredientsData?.meta.range}
      unit={weightUnit}
      disabled={ingredientsLoading || ingredientsError || (ingredientsData?.meta.totalCount ?? 0) === 0}
    />
  );

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
      <FilterBar
        filters={filters}
        filtersKey={filtersKey}
        setTimeRange={setTimeRange}
        setCustomRange={setCustomRange}
        setMealPeriod={setMealPeriod}
        setTeamId={setTeamId}
        rightSlot={exportButton}
      />

      <SummaryCards summary={menuItemsData?.summary ?? null} loading={menuItemsLoading} error={menuItemsError} />

      <Paper variant="outlined" sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2 }}>
          <Tabs value={selectedTab} onChange={(_, value) => handleTabChange(value)} sx={{ minHeight: 48 }}>
            <Tab label={MENU_ITEMS_TAB} value={MENU_ITEMS_TAB} sx={{ textTransform: "none", fontWeight: 600 }} />
            <Tab label={INGREDIENTS_TAB} value={INGREDIENTS_TAB} sx={{ textTransform: "none", fontWeight: 600 }} />
          </Tabs>
          {selectedTab === INGREDIENTS_TAB && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Units
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={weightUnit}
                onChange={(_, value: WeightUnit | null) => value && setWeightUnit(value)}
              >
                <ToggleButton value="lb" sx={{ textTransform: "none", px: 1.5, py: 0.25 }}>
                  lb
                </ToggleButton>
                <ToggleButton value="kg" sx={{ textTransform: "none", px: 1.5, py: 0.25 }}>
                  kg
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
        </Box>
        <Divider />

        {selectedTab === MENU_ITEMS_TAB ? (
          <MenuItemsTable
            data={menuItemsData}
            loading={menuItemsLoading}
            error={menuItemsError}
            sortBy={menuItemsControls.sortBy}
            sortDir={menuItemsControls.sortDir}
            page={menuItemsControls.page}
            pageSize={menuItemsControls.pageSize}
            onSort={menuItemsControls.setSort}
            onPageChange={menuItemsControls.setPage}
            onPageSizeChange={menuItemsControls.setPageSize}
          />
        ) : (
          <IngredientsTable
            data={ingredientsData}
            loading={ingredientsLoading}
            error={ingredientsError}
            unit={weightUnit}
            sortBy={ingredientsControls.sortBy}
            sortDir={ingredientsControls.sortDir}
            page={ingredientsControls.page}
            pageSize={ingredientsControls.pageSize}
            onSort={ingredientsControls.setSort}
            onPageChange={ingredientsControls.setPage}
            onPageSizeChange={ingredientsControls.setPageSize}
          />
        )}
      </Paper>
    </Box>
  );
};
